import type {
  SegmentCondition,
  SegmentFieldDefinition,
  SegmentRuleGroup,
  SegmentSubGroup,
} from '../types/segments.types';

export function createEmptyRuleGroup(): SegmentRuleGroup {
  return {
    combinator: 'and',
    conditions: [],
    groups: [],
  };
}

export function createEmptySubGroup(): SegmentSubGroup {
  return {
    combinator: 'and',
    conditions: [],
  };
}

export function canAddNestedGroup(depth: number): boolean {
  return depth === 0;
}

/** Strip `groups` from sub-groups so the wire payload matches SegmentSubGroup. */
export function toWireRuleGroup(root: SegmentRuleGroup): SegmentRuleGroup {
  return {
    combinator: root.combinator,
    conditions: root.conditions,
    groups: root.groups.map(({ combinator, conditions }) => ({ combinator, conditions })),
  };
}

/**
 * Normalize API / legacy payloads into editor state.
 * Sub-groups may still carry an empty `groups` array from older Mongo documents.
 */
export function normalizeRuleGroupFromApi(rules: SegmentRuleGroup): SegmentRuleGroup {
  return {
    combinator: rules.combinator,
    conditions: Array.isArray(rules.conditions) ? rules.conditions : [],
    groups: (Array.isArray(rules.groups) ? rules.groups : []).map((group) => ({
      combinator: group.combinator,
      conditions: Array.isArray(group.conditions) ? group.conditions : [],
    })),
  };
}

export function emptyConditionForField(field: SegmentFieldDefinition): SegmentCondition {
  return {
    field: field.field,
    operator: field.operators[0] ?? 'eq',
  };
}

export function applyFieldChangeToCondition(
  _condition: SegmentCondition,
  field: SegmentFieldDefinition
): SegmentCondition {
  return emptyConditionForField(field);
}

function isConditionValueComplete(condition: SegmentCondition): boolean {
  const { operator, value, unit } = condition;

  if (operator === 'isset' || operator === 'isnotset') {
    return true;
  }

  if (operator === 'in' || operator === 'notin') {
    return Array.isArray(value) && value.length > 0;
  }

  if (operator === 'inlast') {
    return (
      typeof value === 'number' &&
      Number.isInteger(value) &&
      value > 0 &&
      typeof unit === 'string' &&
      unit.length > 0
    );
  }

  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.length > 0;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return true;
  }

  return false;
}

function isConditionComplete(condition: SegmentCondition): boolean {
  if (!condition.field || !condition.operator) {
    return false;
  }

  return isConditionValueComplete(condition);
}

function evaluateSubGroup(group: SegmentSubGroup): { hasConditions: boolean; allComplete: boolean } {
  const hasConditions = group.conditions.length > 0;
  const allComplete = group.conditions.every(isConditionComplete);
  return { hasConditions, allComplete };
}

function evaluateRuleGroup(group: SegmentRuleGroup): { hasConditions: boolean; allComplete: boolean } {
  const hasConditions = group.conditions.length > 0;
  const allComplete = group.conditions.every(isConditionComplete);

  const nested = group.groups.reduce(
    (acc, child) => {
      const result = evaluateSubGroup(child);
      return {
        hasConditions: acc.hasConditions || result.hasConditions,
        allComplete: acc.allComplete && result.allComplete,
      };
    },
    { hasConditions: false, allComplete: true }
  );

  return {
    hasConditions: hasConditions || nested.hasConditions,
    allComplete: allComplete && nested.allComplete,
  };
}

export function isRuleTreeComplete(group: SegmentRuleGroup): boolean {
  const { hasConditions, allComplete } = evaluateRuleGroup(group);
  return hasConditions && allComplete;
}
